import moment from "moment";
import {APPOINTMENT_VIEWS, FREE_SLOT_INTERVAL_MINUTES} from "./appointments.constants";

export const createYearOptions = (currentYear = new Date().getFullYear()) =>
  Array.from({length: 101}, (_, index) => currentYear - 100 + index);

/** @returns {import("./appointments.types").AppointmentDateRange} */
export const getCalendarDateRange = (view, date) => {
  if (view === APPOINTMENT_VIEWS.WEEK) {
    return {
      startDate: moment(date).startOf("week").toDate(),
      endDate: moment(date).endOf("week").toDate(),
    };
  }

  if (view === APPOINTMENT_VIEWS.MONTH) {
    return {
      startDate: moment(date).startOf("month").toDate(),
      endDate: moment(date).endOf("month").toDate(),
    };
  }

  return {startDate: date, endDate: date};
};

export const navigateCalendarDate = (date, view, direction) =>
  moment(date)
    .add(direction * (view === APPOINTMENT_VIEWS.WEEK ? 7 : 1), "days")
    .toDate();

export const getCalendarDisplayDate = (date, view) => {
  if (view === APPOINTMENT_VIEWS.WEEK || view === APPOINTMENT_VIEWS.MONTH) {
    return moment(date).add(1, "day").toDate();
  }

  return moment(date).toDate();
};

export const shiftCalendarEventDatesForDisplay = (events = [], view) => {
  if (view !== APPOINTMENT_VIEWS.WEEK && view !== APPOINTMENT_VIEWS.MONTH) {
    return events;
  }

  return events.map(event => ({
    ...event,
    start: moment(event.start).add(1, "day").toDate(),
    end: moment(event.end ?? event.start).add(1, "day").toDate(),
  }));
};

export const setTimeWithoutMutation = (date, hours, minutes = 0, seconds = 0) => {
  const result = new Date(date);
  result.setHours(hours, minutes, seconds, 0);
  return result;
};

const CONSULTATION_EVENT_TYPES = Object.freeze({
  "For online consultation": "video",
  "For cabinet consultation": "cabinet",
});

export const parseAppointmentDate = (appointmentDate, appointmentTime) => {
  const datePart = String(appointmentDate ?? "").slice(0, 10);
  const timePart = String(appointmentTime ?? "00:00").slice(0, 5);

  return moment(`${datePart} ${timePart}`, "YYYY-MM-DD HH:mm", true);
};

/**
 * Converts the appointments API payload into calendar-focused data. Keeping this
 * translation outside React prevents server field names from leaking throughout
 * the presentation layer.
 */
export const normalizeAppointmentsResponse = ({
  data = [],
  interval = [],
  DoctorDelays: doctorDelays = [],
} = {}) => {
  const absentEventIds = new Set();
  const events = data.filter(item => Number(item.status) !== 2).map(item => {
    if (item.status === 3) absentEventIds.add(item.id);

    const start = parseAppointmentDate(item.appointment_date, item.time);

    return {
      id: item.id,
      patient_id: item.patient_id,
      doctor_id: item.doctor_id,
      status: item.status,
      consultation_type: item.consultation_type,
      title: item.name,
      time: item.time,
      questions: item.questions,
      confirm: item.confirm,
      start: start.toDate(),
      end: start.clone().add(15, "minutes").toDate(),
      type: CONSULTATION_EVENT_TYPES[item.consultation_type] ?? "",
      orders: item.orders,
      priority: item.userpriority,
      supports: item.usersupport,
      finish: item.finish,
      refer_name: item.refer_name,
      patient_details: item.patient_details,
      interval,
    };
  });

  const intervals = interval.map(item => ({
    startTime: item.start_time,
    endTime: item.end_time,
    has_interval: item.has_interval,
    visitor: item.visitor,
    day: item.day,
  }));

  const currentDelay = doctorDelays.find(item => item?.start_time);
  const delay = currentDelay
    ? {
        id: currentDelay.id,
        delayStartTime: currentDelay.start_time,
        delayDuration: currentDelay.delay,
        delayReason: currentDelay.reason,
      }
    : null;

  return {events, intervals, absentEventIds, delay};
};

export const validateDelayForm = formData => {
  const errors = {};
  const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;

  if (!timePattern.test(formData.delayStartTime)) {
    errors.delayStartTime = "Please enter a valid start time (HH:MM).";
  }
  if (!timePattern.test(formData.delayDuration)) {
    errors.delayDuration = "Please enter a valid duration time (HH:MM).";
  }
  if (!formData.delayReason) {
    errors.delayReason = "Please select a reason.";
  }

  return errors;
};

export const generateTimeSlots = (startTime, endTime, interval, unit) => {
  const slots = [];
  const cursor = moment(startTime, "HH:mm");
  const end = moment(endTime, "HH:mm");
  const incrementUnit = unit === "Hour" ? "hours" : "minutes";

  while (cursor.isBefore(end)) {
    slots.push(cursor.format("HH:mm"));
    cursor.add(interval, incrementUnit);
  }

  return slots;
};

export const isAppointmentDelayed = (appointmentStart, delay) => {
  if (!delay?.delayStartTime || !delay?.delayDuration) return false;

  const [hours = 0, minutes = 0] = delay.delayDuration.split(":").map(Number);
  const start = moment(delay.delayStartTime, "HH:mm");
  const end = start.clone().add(hours, "hours").add(minutes, "minutes");
  const appointmentTime = moment(moment(appointmentStart).format("HH:mm"), "HH:mm");

  return appointmentTime.isBetween(start, end, null, "[)");
};

export const calculateDelayEndTime = (startTime, duration) => {
  const [hours = 0, minutes = 0] = duration.split(":").map(Number);
  return moment(startTime, "HH:mm").add(hours, "hours").add(minutes, "minutes").format("HH:mm");
};

const overlapsSlot = (event, slotStart, slotEnd) => {
  // Interval placeholder events (for example "09:00-10:00") describe capacity
  // rather than a single occupied appointment and must not block the slot.
  if (event.time?.includes("-")) return false;

  return moment(event.start).isBefore(slotEnd) && moment(event.end).isAfter(slotStart);
};

/**
 * Builds the free-slot events consumed by react-big-calendar. Availability
 * intervals are attached to their interior slots so week/month renderers can
 * display capacity without knowing the server response shape.
 */
export const generateFreeSlots = (dates, events, intervalsByDay) => {
  const slots = [];

  dates.forEach(date => {
    const startOfDay = moment(date).startOf("day");
    const endOfDay = moment(date).endOf("day");
    const dayName = moment(date).format("dddd").toLowerCase();

    for (
      let cursor = startOfDay.clone();
      cursor.isBefore(endOfDay);
      cursor.add(FREE_SLOT_INTERVAL_MINUTES, "minutes")
    ) {
      const slotStart = cursor.clone();
      const slotEnd = cursor.clone().add(FREE_SLOT_INTERVAL_MINUTES, "minutes");
      const slotTime = slotStart.format("HH:mm");
      const currentInterval = intervalsByDay.find(interval =>
        interval.day === dayName &&
        moment(slotTime, "HH:mm").isBetween(
          moment(interval.startTime, "HH:mm"),
          moment(interval.endTime, "HH:mm"),
          null,
          "()",
        ),
      );
      const isFree = !events.some(event => overlapsSlot(event, slotStart, slotEnd));

      if (currentInterval) {
        slots.push({
          start: slotStart.toDate(),
          end: slotEnd.toDate(),
          type: "free",
          weekIntervals: intervalsByDay,
          insideInterVal: 1,
          currentInterval,
        });
      } else if (isFree) {
        slots.push({
          start: slotStart.toDate(),
          end: slotEnd.toDate(),
          type: "free",
          weekIntervals: intervalsByDay,
        });
      }
    }

    const finalSlotStart = endOfDay.clone().subtract(FREE_SLOT_INTERVAL_MINUTES - 1, "minutes");
    if (!events.some(event => overlapsSlot(event, finalSlotStart, endOfDay))) {
      slots.push({
        start: finalSlotStart.toDate(),
        end: endOfDay.toDate(),
        type: "free",
      });
    }
  });

  return slots;
};

export const groupEventsByDate = events =>
  events.reduce((groupedEvents, event) => {
    const dateKey = moment(event.start).format("YYYY-MM-DD");
    groupedEvents[dateKey] ??= [];
    groupedEvents[dateKey].push(event);
    return groupedEvents;
  }, {});

export const getCalendarClass = view => {
  const classes = {
    [APPOINTMENT_VIEWS.DAY]: "day-view",
    [APPOINTMENT_VIEWS.WEEK]: "week-view",
    [APPOINTMENT_VIEWS.MONTH]: "month-view",
  };

  return classes[view] ?? "";
};
